import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Flex,
  Icon,
  Input,
  Link as ChakraLink,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ElementType, ReactNode, useMemo, useState } from 'react';
import { FiDatabase, FiGrid, FiSearch, FiStar } from 'react-icons/fi';
import { RiMenu2Fill } from 'react-icons/ri';
import { Link, useLocation } from 'react-router-dom';

import { useCan } from '../../../../../hooks/useCan';
import {
  filterBiReports,
  groupBiReports,
  useBiGroups,
  useVisibleBiReports,
} from '../../../../../hooks/useBi';
import { BiReport } from '../../../../../services/biService';
import {
  resolveAppearance,
  sortGroups,
} from '../../../../../utils/biAppearance';
import { matchesSearch } from '../../../../../utils/biCatalog';
import { NavLink } from '../../NavLink';
import { NavSection } from '../../NavSection';

// Mesmo botão de grupo do menu antigo (gray.900, azul quando aberto).
const groupButtonStyle = {
  bg: 'gray.900',
  borderWidth: '1px',
  borderColor: 'gray.800',
  borderRadius: 'md',
  fontWeight: '500',
  _hover: { bg: 'gray.650' },
  _expanded: { bg: 'blue.400', color: 'white' },
};

function ItemLink({
  to,
  isActive = false,
  children,
}: {
  to: string;
  isActive?: boolean;
  children: ReactNode;
}) {
  return (
    <ChakraLink
      as={Link}
      to={to}
      display="block"
      px="3"
      py="2"
      borderRadius="md"
      fontSize="sm"
      color={isActive ? 'blue.500' : 'gray.50'}
      fontWeight={isActive ? 'medium' : 'normal'}
      _hover={{ bg: 'gray.650', textDecoration: 'none' }}
    >
      {children}
    </ChakraLink>
  );
}

function ReportLink({
  report,
  isActive,
}: {
  report: BiReport;
  isActive: boolean;
}) {
  return (
    <ItemLink
      to={`/bi-management/renderization/${report.id}`}
      isActive={isActive}
    >
      {report.reportName}
    </ItemLink>
  );
}

function GroupButton({
  label,
  icon,
  color,
}: {
  label: string;
  icon: ElementType;
  color: string;
}) {
  return (
    <AccordionButton {...groupButtonStyle}>
      <Flex flex="1" align="center" gridGap="2" textAlign="left" minW="0">
        <Icon as={icon} color={`${color}.400`} flexShrink={0} />
        <Text isTruncated>{label}</Text>
      </Flex>
      <AccordionIcon />
    </AccordionButton>
  );
}

export function SidebarNavBi() {
  const { pathname } = useLocation();
  const { reports, isLoading, isError } = useVisibleBiReports();
  const { byName } = useBiGroups();
  const [search, setSearch] = useState('');

  const userCanSeeEditEnergy = useCan({ permissions: ['edit_energy'] });
  const userCanSeeClimateResearch = useCan({
    permissions: ['edit_climate_research'],
  });
  const userCanSeeDataTi = useCan({ permissions: ['ti_data'] });

  const currentId = pathname.startsWith('/bi-management/renderization/')
    ? pathname.split('/')[3]
    : '';

  const groups = useMemo(
    () => sortGroups(groupBiReports(filterBiReports(reports, search)), byName),
    [reports, search, byName],
  );
  const favorites = reports.filter(report => report.isFavorite);

  // Telas de "Gestão de dados", com os mesmos nomes e permissões de antes.
  const dataGroups = [
    {
      label: 'Resultado financeiro',
      to: '/management-ppr/report-income-statement',
      item: 'Demonstrativo de resultados',
      allowed: true,
    },
    {
      label: 'Map. de Processos',
      to: '/process-management',
      item: 'Gerenciar mapeamento',
      allowed: userCanSeeDataTi,
    },
    {
      label: 'Rep. Custos Energia',
      to: '/management-energy/energy-table',
      item: 'Gerenciar custos de energia',
      allowed: userCanSeeEditEnergy,
    },
    {
      label: 'Pesquisa de clima',
      to: '/climate-research-management',
      item: 'Gerenciar pesquisas',
      allowed: userCanSeeClimateResearch,
    },
  ].filter(
    group =>
      group.allowed && matchesSearch(`${group.label} ${group.item}`, search),
  );

  // Grupos abertos guardados pelo nome (a posição muda quando um grupo entra
  // ou sai da lista). Começa com o grupo do relatório que está na tela; com
  // busca, todos os grupos encontrados ficam abertos.
  const [expanded, setExpanded] = useState<string[] | null>(null);
  const currentGroup = groups.find(group =>
    group.reports.some(report => report.id === currentId),
  )?.group;
  const openGroups =
    expanded ?? (currentGroup ? [currentGroup] : ([] as string[]));
  const accordionIndex = search.trim()
    ? groups.map((_, index) => index)
    : groups
        .map((group, index) => (openGroups.includes(group.group) ? index : -1))
        .filter(index => index >= 0);

  const handleAccordionChange = (index: number | number[]) => {
    if (search.trim()) {
      return;
    }

    const indexes = Array.isArray(index) ? index : [index];

    setExpanded(
      indexes.map(position => groups[position]?.group).filter(Boolean),
    );
  };

  return (
    <Stack spacing="8" align="stretch">
      <NavSection title="GERAL">
        <NavLink to="/menu" icon={RiMenu2Fill}>
          Menu
        </NavLink>
        <NavLink to="/bi-management/dashboard" icon={FiGrid}>
          Todos os relatórios
        </NavLink>
      </NavSection>

      <Box position="relative">
        <Icon
          as={FiSearch}
          position="absolute"
          left="3"
          top="50%"
          transform="translateY(-50%)"
          color="gray.400"
          zIndex={1}
        />
        <Input
          size="sm"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Buscar relatório"
          aria-label="Buscar relatório"
          pl="9"
        />
      </Box>

      {!search.trim() && favorites.length > 0 && (
        <NavSection title="FAVORITOS">
          <Stack spacing="0">
            {favorites.map(report => (
              <Box key={report.id} display="flex" alignItems="center">
                <Icon
                  as={FiStar}
                  color="yellow.400"
                  fill="currentColor"
                  mr="1"
                />
                <Box flex="1" minW="0">
                  <ReportLink
                    report={report}
                    isActive={report.id === currentId}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </NavSection>
      )}

      <NavSection title="GRUPOS DE ANÁLISES">
        {isLoading && (
          <Center>
            <Spinner size="lg" />
          </Center>
        )}

        {isError && (
          <Text fontSize="sm" color="gray.400">
            Não foi possível carregar os relatórios.
          </Text>
        )}

        {!isLoading && !isError && groups.length === 0 && (
          <Text fontSize="sm" color="gray.500">
            {search.trim()
              ? 'Nenhum relatório encontrado.'
              : 'Nenhum relatório liberado para você.'}
          </Text>
        )}

        <Accordion
          allowMultiple
          index={accordionIndex}
          onChange={handleAccordionChange}
        >
          {groups.map(group => {
            const appearance = resolveAppearance(
              null,
              byName[group.group],
              group.group,
            );

            return (
              <AccordionItem key={group.group} border="none" mb="2">
                <GroupButton
                  label={group.group}
                  icon={appearance.icon}
                  color={appearance.color}
                />
                <AccordionPanel px="0" pt="1" pb="2">
                  {group.reports.map(report => (
                    <ReportLink
                      key={report.id}
                      report={report}
                      isActive={report.id === currentId}
                    />
                  ))}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </NavSection>

      {dataGroups.length > 0 && (
        <NavSection title="GESTÃO DE DADOS">
          <Accordion allowMultiple>
            {dataGroups.map(group => (
              <AccordionItem key={group.to} border="none" mb="2">
                <GroupButton
                  label={group.label}
                  icon={FiDatabase}
                  color="green"
                />
                <AccordionPanel px="0" pt="1" pb="2">
                  <ItemLink to={group.to} isActive={pathname === group.to}>
                    {group.item}
                  </ItemLink>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </NavSection>
      )}
    </Stack>
  );
}
